import { type ReactNode, useEffect } from "react";
import { Link, Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse } from "react-router";

import type { Route } from "./+types/root";
import "~/styles/globals.css";
// Zod グローバル設定 (ja ロケール + 必須 string 未入力の friendly メッセージ)。副作用 import。
import "~/lib/zod-config";
import { QueryClientProvider } from "@tanstack/react-query";
import {
    BadRequestError,
    ConflictError,
    ForbiddenError,
    LockedError,
    NotFoundError,
    UnauthorizedError,
    UserDeletedError,
    queryClient,
} from "./client";
import { Loading } from "./components/Loading";
import { ProcessingOverlay } from "./components/ProcessingOverlay";
import { PendingActionProvider } from "./hooks/usePendingAction";
import { auth0Service } from "./services/auth0";
import { initDatadogLogs, logError } from "./services/datadog";
import { SITE_TITLE } from "./utils/pageTitle";

initDatadogLogs();

// ここでの title は初期値。 ページごとのタイトルは各ページの useSetPageTitle が設定する。
export const meta: Route.MetaFunction = () => [
    { title: SITE_TITLE },
    { charSet: "utf-8" },
    {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
    },
];

export const links: Route.LinksFunction = () => [
    {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
    },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap",
    },
    {
        rel: "icon",
        type: "image/png",
        href: "/favicon.png",
    },
];

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en">
            <head>
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <PendingActionProvider>
                <Outlet />
                {/* POST 通信が長引いたときだけ全画面をグレーアウトする。モーダルより上 (z-[60]) に置くため root で描画する */}
                <ProcessingOverlay />
            </PendingActionProvider>
        </QueryClientProvider>
    );
}

export function HydrateFallback() {
    return <Loading isFullScreen={true} />;
}

// エラー画面の共通シェル。タイトル + 任意の本文 (メッセージ / アクション) を中央カードで表示する。
function ErrorScreen({ title, children }: { title: ReactNode; children?: ReactNode }) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-github-canvas p-4">
            <div className="rounded-md bg-github-canvas-subtle p-12 shadow-lg bg-red-100 border border-red-400 rounded text-red-700 max-w-2xl w-full text-center">
                <h1 className="text-2xl font-bold mb-4">{title}</h1>
                {children}
            </div>
        </div>
    );
}

// API エラー (client.ts の *Error クラス) はサーバーの requestId を保持する。 構造化ログ (Datadog) 用に取り出す。
const getRequestId = (error: unknown): string | undefined => {
    if (error instanceof Error && "requestId" in error) {
        const { requestId } = error;
        return typeof requestId === "string" ? requestId : undefined;
    }
    return undefined;
};

// 期待されたクライアントエラー (ルート未マッチ / 401 / 403 / 404 / 409 / 423 / 400)。 UX として画面表示する
// もので調査対象ではないため、 構造化ログ (Datadog) には送らない。 これ以外 (500 / 想定外 = クラッシュ) を送る。
const isExpectedClientError = (error: unknown): boolean =>
    isRouteErrorResponse(error) ||
    error instanceof BadRequestError ||
    error instanceof UnauthorizedError ||
    error instanceof ForbiddenError ||
    error instanceof NotFoundError ||
    error instanceof UserDeletedError ||
    error instanceof ConflictError ||
    error instanceof LockedError;

const RED_BUTTON = "mt-4 px-6 py-3 bg-red-600 text-white rounded hover:bg-red-700 text-lg";

const HomeLink = () => (
    <div className="flex justify-center">
        <Link to="/" className={RED_BUTTON}>
            Topに戻る
        </Link>
    </div>
);

// 「内容 (message) + 技術詳細 (code / requestId / name 等)」を表示するブロック。 route 未マッチ以外の
// 全エラーで共通表示し、 問い合わせ・調査に必要な requestId を含める。
function ErrorDetail({ error }: { error: unknown }) {
    const message = error instanceof Error ? error.message : "予期せぬエラーが発生しました";
    const context =
        error instanceof Error
            ? Object.entries(error)
                  .map(([key, value]) => `- ${key}: ${value}`)
                  .join("\n")
            : "";
    return (
        <pre className="text-left bg-red-50 p-4 rounded border border-red-300 whitespace-pre-wrap break-words mb-4 font-mono text-sm">
            {`内容: ${message}`}
            {context && `\n\n詳細:\n${context}`}
        </pre>
    );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    const requestId = getRequestId(error);

    // 500 / 想定外エラー (クラッシュ) のみ構造化ログを送る (期待されたクライアントエラーは除外)。
    // loader/action 由来の捕捉済みエラーは forwardErrorsToLogs では拾えないため明示送信する。
    useEffect(() => {
        if (!isExpectedClientError(error)) {
            logError(error, { requestId });
        }
    }, [error, requestId]);

    // ルート未マッチ (React Router の Response エラー)。
    if (isRouteErrorResponse(error)) {
        return (
            <ErrorScreen
                title={
                    <>
                        ページが見つかりませんでした。
                        <br />
                        URLを確認してください。
                    </>
                }
            >
                <HomeLink />
            </ErrorScreen>
        );
    }

    // 401: 認証切れ・未認証 → 再ログインを促す。
    if (error instanceof UnauthorizedError) {
        return (
            <ErrorScreen
                title={
                    <>
                        ログインの有効期限が切れている可能性があります。
                        <br />
                        再度ログインしてください。
                    </>
                }
            >
                <ErrorDetail error={error} />
                <div className="flex justify-center">
                    <button type="button" onClick={() => auth0Service.login()} className={RED_BUTTON}>
                        再ログイン
                    </button>
                </div>
            </ErrorScreen>
        );
    }

    // 403 / USER_DELETED: 論理削除されたユーザー。再ログインでは直らないので「削除されている」と伝え、ログアウトだけ出す
    // (「Topに戻る」は /api/me で同じ画面に戻るだけなので出さない)。見た目は 404 と同じ ErrorScreen。
    if (error instanceof UserDeletedError) {
        return (
            <ErrorScreen title="ユーザーが削除されています。">
                <ErrorDetail error={error} />
                <div className="flex justify-center">
                    <button type="button" onClick={() => auth0Service.logout()} className={RED_BUTTON}>
                        ログアウト
                    </button>
                </div>
            </ErrorScreen>
        );
    }

    // 403: 権限不足。サーバーからの説明 (error.message) を添える。
    if (error instanceof ForbiddenError) {
        return (
            <ErrorScreen title="権限がありません。">
                <ErrorDetail error={error} />
                <HomeLink />
            </ErrorScreen>
        );
    }

    // 404: 対象データが存在しない。
    if (error instanceof NotFoundError) {
        return (
            <ErrorScreen title="対象が見つかりませんでした。">
                <ErrorDetail error={error} />
                <HomeLink />
            </ErrorScreen>
        );
    }

    // 400: 入力不正。リトライでは直らないため、サーバーからの説明 (error.message) を添えて内容確認を促す。
    if (error instanceof BadRequestError) {
        return (
            <ErrorScreen title="リクエストの内容に誤りがあります。">
                <ErrorDetail error={error} />
                <HomeLink />
            </ErrorScreen>
        );
    }

    // 409: 編集競合。別ユーザーの編集等、回復可能な競合であることを伝える。
    if (error instanceof ConflictError) {
        return (
            <ErrorScreen title="操作が競合しました。">
                <ErrorDetail error={error} />
                <HomeLink />
            </ErrorScreen>
        );
    }

    // 423: ロック。編集完了ロック等で操作できないことを伝える。
    if (error instanceof LockedError) {
        return (
            <ErrorScreen title="対象がロックされているため操作できません。">
                <ErrorDetail error={error} />
                <HomeLink />
            </ErrorScreen>
        );
    }

    // それ以外 (500 / 503 / 想定外) は詳細を添えて汎用表示する。
    return (
        <ErrorScreen
            title={
                <>
                    エラーが発生しました。
                    <br />
                    しばらくしてから再度お試しください。
                </>
            }
        >
            <ErrorDetail error={error} />
            <HomeLink />
        </ErrorScreen>
    );
}
